/**
 * Tweet Text Parser
 *
 * Shared utility for parsing tweet text with entities (mentions, hashtags, URLs)
 * and converting them to HTML with proper links and formatting.
 *
 * Uses @ambassify/twitter-text library for robust Twitter-compliant text parsing.
 */

import { regex } from 'arkregex';
import { compact } from 'es-toolkit';
import { z } from 'zod';
import { isUrlExcludedFromLinkPreview } from '@/lib/link-preview/exclusions';
import { isNonEmptyArray } from '@/lib/utils/array-helpers';
import { escapeRegExp } from '@/lib/utils/regex';
import { getRecordValue, isRecord } from '@/lib/utils/type-guards';
import { toTrimmedNonEmptyStringOrUndefined } from '@/lib/utils/type-guards.ts';
import type { TweetEntities } from '@/types/tweet';
import type { LinkMetadata } from './fetch-metadata';
import { extractTweetUrls } from './tweet-utils';
import type { TwitterUrlEntity } from './twitter-text-adapter';
import { isTwitterImageUrl, parseTweetWithEntities } from './twitter-text-adapter';

// Create URL schema from the TweetEntitiesSchema urls array
const TweetUrlSchema = z.object({
  url: z.string(),
  expandedUrl: z.string(),
  displayUrl: z.string(),
  mediaKey: z.string().optional(),
  title: z.string().optional(),
  description: z.string().nullable().optional(),
  unwoundUrl: z.string().optional(),
  start: z.number().optional(),
  end: z.number().optional(),
});

// Type definitions derived from schemas
export type TweetUrl = z.infer<typeof TweetUrlSchema>;

export interface TweetMedia {
  height?: number;
  mediaKey: string;
  type?: 'photo' | 'video' | 'animated_gif';
  url?: string;
  width?: number;
}

export interface ParsedTweetContent {
  cardUrls: TweetUrl[];
  html: string;
  inlineTweetUrls: Array<{ url: string; username: string; tweetId: string }>;
}

// Flexible tweet type that accepts various tweet shapes
export interface ParseableTweet {
  entities?: TweetEntities;
  mediaItems?: TweetMedia[];
  metadata?: Record<string, LinkMetadata>;
  text: string;
}

// Normalize a raw URL entity (snake_case or camelCase) to camelCase for parsing
function normalizeUrlEntity(raw: unknown): TweetUrl | null {
  if (!isRecord(raw)) {
    return null;
  }

  const value = raw;

  const urlValue = getRecordValue(value, 'url');
  const url = typeof urlValue === 'string' && urlValue.length > 0 ? urlValue : undefined;
  if (!url) {
    return null;
  }

  const readString = (candidate: unknown): string | undefined =>
    typeof candidate === 'string' && candidate.length > 0 ? candidate : undefined;

  const expandedUrl =
    readString(getRecordValue(value, 'expandedUrl')) ??
    readString(getRecordValue(value, 'expanded_url')) ??
    readString(getRecordValue(value, 'unwoundUrl')) ??
    readString(getRecordValue(value, 'unwound_url')) ??
    url;

  const displayUrl =
    readString(getRecordValue(value, 'displayUrl')) ?? readString(getRecordValue(value, 'display_url')) ?? url;

  let description: string | null | undefined;
  const descriptionValue = getRecordValue(value, 'description');
  if (typeof descriptionValue === 'string') {
    description = descriptionValue;
  } else if (descriptionValue === null) {
    description = null;
  }

  const result: TweetUrl = {
    url,
    expandedUrl,
    displayUrl,
  };

  const title = readString(getRecordValue(value, 'title'));
  if (title) {
    result.title = title;
  }
  if (description !== undefined) {
    result.description = description;
  }
  const unwoundUrl =
    readString(getRecordValue(value, 'unwoundUrl')) ?? readString(getRecordValue(value, 'unwound_url'));
  if (unwoundUrl) {
    result.unwoundUrl = unwoundUrl;
  }
  const startValue = getRecordValue(value, 'start');
  if (typeof startValue === 'number') {
    result.start = startValue;
  }
  const endValue = getRecordValue(value, 'end');
  if (typeof endValue === 'number') {
    result.end = endValue;
  }

  return result;
}

const MEDIA_STATUS_PATTERN = regex.as(
  String.raw`https?:\/\/(?:x|twitter)\.com\/[^/]+\/status(?:es)?\/\d+\/(?:photo|video)\/`,
  'i'
);
const TCO_ANCHOR_CAPTURE_PATTERN = regex.as(String.raw`<a[^>]*href="(https?:\/\/t\.co\/[A-Za-z0-9]+)"[^>]*>[^<]*<\/a>`);
const MEDIA_FILE_EXTENSION_PATTERN = regex.as(String.raw`\.(?:jpg|jpeg|png|gif|webp|mp4|mov|avi)$`, 'i');
const TRAILING_BREAK_PATTERN = regex.as(String.raw`(?:\s*<br(?:\s+[^>]*)?>\s*)+$`, 'gi');

const stripProtocolPrefix = (value: string): string => {
  let next = value;
  let lower = next.toLowerCase();
  if (lower.startsWith('https://')) {
    next = next.slice(8);
    lower = next.toLowerCase();
  } else if (lower.startsWith('http://')) {
    next = next.slice(7);
    lower = next.toLowerCase();
  }
  return lower.startsWith('www.') ? next.slice(4) : next;
};

function normalizeUrlForComparison(value: string | undefined): string | null {
  if (!value) {
    return null;
  }
  try {
    const url = new URL(value);
    const path = url.pathname.endsWith('/') ? url.pathname.slice(0, -1) : url.pathname;
    return `${url.hostname.toLowerCase()}${path}`;
  } catch {
    return value.toLowerCase();
  }
}

function removeUrlTokensFromHtml(html: string, tokens: Iterable<string>): string {
  let next = html;
  for (const token of tokens) {
    if (typeof token !== 'string' || token.length === 0) {
      continue;
    }
    const escaped = escapeRegExp(token);
    const anchorPattern = regex.as(String.raw`<a\b[^>]*href="${escaped}"[^>]*>[\s\S]*?<\/a>`, 'gi');
    next = next.replaceAll(anchorPattern, '');
    next = next.replaceAll(regex.as(escaped, 'gi'), '');
    const stripped = stripProtocolPrefix(token);
    if (stripped && stripped !== token) {
      const strippedEscaped = escapeRegExp(stripped);
      next = next.replaceAll(regex.as(strippedEscaped, 'gi'), '');
    }
  }
  return next;
}

function collectMediaUrlTokens(entity: unknown): string[] {
  if (!isRecord(entity)) {
    return [];
  }
  const record = entity;
  const urlValue = getRecordValue(record, 'url');
  const url = typeof urlValue === 'string' ? urlValue : undefined;
  const expanded = toTrimmedNonEmptyStringOrUndefined(getRecordValue(record, 'expandedUrl'));
  const display = toTrimmedNonEmptyStringOrUndefined(getRecordValue(record, 'displayUrl'));
  const mediaKeyValue = getRecordValue(record, 'mediaKey');
  const hasMediaAssociation = typeof mediaKeyValue === 'string';
  const isStatusMedia = typeof expanded === 'string' ? MEDIA_STATUS_PATTERN.test(expanded) : false;
  const isTwitterImage = isTwitterImageUrl(expanded) || isTwitterImageUrl(display);

  if (!(hasMediaAssociation || isStatusMedia || isTwitterImage)) {
    return [];
  }

  return [url, expanded, display]
    .map((value) => toTrimmedNonEmptyStringOrUndefined(value))
    .filter((value): value is string => value !== undefined);
}

/**
 * Parse tweet text and convert entities to HTML
 *
 * This function:
 * 1. Extracts all entities using twitter-text (mentions, hashtags, URLs)
 * 2. Processes URLs (removes media URLs, identifies tweet URLs, creates link tags)
 * 3. Converts mentions to links with data attributes for hover previews
 * 4. Converts hashtags to styled spans
 * 5. Returns HTML string and extracted URLs for rendering cards
 *
 * @param tweet - The tweet object with text and optional entities
 * @returns ParsedTweetContent with HTML and extracted URLs
 */
/* biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Centralized tweet parsing with multiple small branches is intentional; splitting harms readability and increases duplication. */
export function parseTweetText(tweet: ParseableTweet): ParsedTweetContent {
  const { text, mediaItems, metadata, entities: apiEntities } = tweet;
  const normalizedMediaItems = Array.isArray(mediaItems) ? compact(mediaItems) : [];

  // Parse with twitter-text adapter
  // Normalize API entities for adapter consumption to enrich t.co links
  const normalizedApiUrls = (apiEntities?.urls || [])
    .map((e) => normalizeUrlEntity(e))
    .filter((entity): entity is TweetUrl => entity !== null);

  const mediaLinkedTokens = new Set<string>();
  if (Array.isArray(apiEntities?.urls)) {
    for (const raw of apiEntities.urls) {
      for (const token of collectMediaUrlTokens(raw)) {
        mediaLinkedTokens.add(token);
      }
    }
  }

  const parsed = parseTweetWithEntities(text, {
    extractUrlsWithoutProtocol: false,
    htmlEscapeNonEntities: true,
    targetBlank: true,
    usernameUrlBase: '/@',
    metadata: metadata ?? {},
    // Pass API url entities so adapter can use expandedUrl/displayUrl for classification
    urlEntities: (() => {
      const map: Record<string, TwitterUrlEntity> = {};
      for (const candidate of normalizedApiUrls) {
        const urlEntity = TweetUrlSchema.safeParse(candidate);
        if (urlEntity.success && urlEntity.data.url) {
          const { url, expandedUrl, displayUrl } = urlEntity.data;
          map[url] = {
            url,
            expanded_url: expandedUrl ?? url,
            display_url: displayUrl ?? url,
            indices: [0, 0],
            hashtag: undefined,
            screenName: undefined,
            listSlug: undefined,
            cashtag: undefined,
          } satisfies TwitterUrlEntity;
        }
      }
      return map;
    })(),
  });

  let { html, cardUrls: twitterTextCardUrls, inlineTweetUrls } = parsed;

  // Ensure downstream consumers (InlineTweet) always receive a concrete status id.
  inlineTweetUrls = inlineTweetUrls
    .map((item) => ({
      ...item,
      tweetId: typeof item.tweetId === 'string' ? item.tweetId.trim() : '',
    }))
    .filter((item) => item.tweetId.length > 0);

  const inlineTweetTargets = new Set(
    inlineTweetUrls
      .map((item) => normalizeUrlForComparison(item.url))
      .filter((value): value is string => typeof value === 'string' && value.length > 0)
  );

  if (inlineTweetTargets.size > 0) {
    const inlineEntityTokens = new Set<string>();
    for (const entity of parsed.entities) {
      if (typeof entity.url !== 'string') {
        continue;
      }
      const urlEntity: TwitterUrlEntity = { ...entity, url: entity.url };
      const candidateUrl = urlEntity.expanded_url || urlEntity.url;
      const key = normalizeUrlForComparison(candidateUrl);
      if (!(key && inlineTweetTargets.has(key))) {
        continue;
      }
      for (const token of [urlEntity.url, urlEntity.expanded_url, urlEntity.display_url]) {
        if (typeof token === 'string' && token.length > 0) {
          inlineEntityTokens.add(token);
        }
      }
    }

    if (inlineEntityTokens.size > 0) {
      html = removeUrlTokensFromHtml(html, [...inlineEntityTokens]);
    }

    twitterTextCardUrls = twitterTextCardUrls.filter((candidate) => {
      const normalized = normalizeUrlEntity(candidate);
      if (!normalized) {
        return true;
      }
      const candidateUrl = normalized.unwoundUrl || normalized.expandedUrl || normalized.displayUrl || normalized.url;
      const key = normalizeUrlForComparison(candidateUrl);
      return !(key && inlineTweetTargets.has(key));
    });

    if (Array.isArray(apiEntities?.urls)) {
      for (const raw of apiEntities.urls) {
        const normalized = normalizeUrlEntity(raw);
        if (!normalized) {
          continue;
        }
        const candidateUrl = normalized.unwoundUrl || normalized.expandedUrl || normalized.displayUrl || normalized.url;
        const key = normalizeUrlForComparison(candidateUrl);
        if (!(key && inlineTweetTargets.has(key))) {
          continue;
        }

        const tokens = [normalized.url, normalized.expandedUrl, normalized.displayUrl].filter(
          (value): value is string => typeof value === 'string' && value.length > 0
        );
        if (tokens.length > 0) {
          html = removeUrlTokensFromHtml(html, tokens);
        }
      }
    }
  }

  // If we have API-provided entities with URLs, check for tweet URLs
  if (apiEntities?.urls) {
    const apiTweetUrls = extractTweetUrls(
      apiEntities.urls.map((u) => ({
        url: u.url,
        expanded_url: u.expandedUrl ?? u.url,
        display_url: u.displayUrl ?? u.url,
      }))
    );
    for (const tweetUrl of apiTweetUrls) {
      if (!inlineTweetUrls.some((t) => t.tweetId === tweetUrl.tweetId)) {
        inlineTweetUrls.push(tweetUrl);
      }
    }
  }

  // Convert to TweetUrl using adapter-enriched entities (expandedUrl/displayUrl when provided by API)
  const cardUrls = twitterTextCardUrls
    .map((u) => {
      // Try direct validation first; if it fails, normalize and try again
      let validatedUrl = TweetUrlSchema.safeParse(u);
      if (!validatedUrl.success) {
        const normalized = normalizeUrlEntity(u);
        if (normalized) {
          validatedUrl = TweetUrlSchema.safeParse(normalized);
        }
      }
      if (validatedUrl.success) {
        const data = validatedUrl.data;
        return {
          url: data.url,
          displayUrl: data.displayUrl || data.url,
          expandedUrl: data.expandedUrl || data.url,
          title: data.title,
          description: data.description ?? null,
          unwoundUrl: data.unwoundUrl,
          start: data.start,
          end: data.end,
        };
      }
      // Fallback for invalid entities - safely extract URL field
      const fallbackResult = z.object({ url: z.string() }).safeParse(u);
      if (fallbackResult.success) {
        return {
          url: fallbackResult.data.url,
          displayUrl: fallbackResult.data.url,
          expandedUrl: fallbackResult.data.url,
          title: undefined,
          description: null,
          unwoundUrl: undefined,
          start: undefined,
          end: undefined,
        };
      }
      // Ultimate fallback - skip invalid entities
      return null;
    })
    .filter((url): url is NonNullable<typeof url> => url !== null)
    // Exclude URLs that should remain inline (no rich preview card)
    .filter((u) => {
      const candidate = u.expandedUrl || u.url;
      return typeof candidate === 'string' ? !isUrlExcludedFromLinkPreview(candidate) : true;
    });

  // Do not transform card URLs into inline tweets based on metadata.
  // We keep cardUrls as-is (subject to filtering rules below) to avoid
  // unexpected inline tweet skeletons for simple links.

  // Handle Twitter image URLs with media items
  // Intent: only strip pic.twitter.com anchors if we will render real images.
  // If the API did not provide hard photo URLs, keep the link visible.
  // Only strip pic.twitter.com anchors if we actually have renderable media URLs.
  const hasRenderableMedia = normalizedMediaItems.some((item) => {
    const isPhotoLike = !item.type || item.type === 'photo';
    return isPhotoLike && typeof item.url === 'string' && item.url.length > 0;
  });

  if (hasRenderableMedia && isNonEmptyArray(normalizedMediaItems)) {
    // Remove pic.twitter.com/pic.x.com URLs if media is present
    // Check both the original URL and expanded URL for Twitter image patterns
    for (const entity of parsed.entities) {
      // Entities coming from twitter-text adapter are snake_case.
      // Normalize to our canonical camelCase shape before validation.
      const normalized = normalizeUrlEntity(entity);
      if (!normalized) {
        continue;
      }
      const validatedEntity = TweetUrlSchema.safeParse(normalized);
      if (!validatedEntity.success) {
        continue;
      }

      const data = validatedEntity.data;
      const candidates = [data.url, data.expandedUrl || data.url, data.displayUrl || data.url].filter(
        (value): value is string => typeof value === 'string' && value.length > 0
      );
      if (candidates.some((candidate) => mediaLinkedTokens.has(candidate))) {
        html = removeUrlTokensFromHtml(html, candidates);
      }
    }

    // Also check API entities in case they weren't caught by twitter-text
    if (apiEntities?.urls) {
      for (const urlEntity of apiEntities.urls) {
        const mediaTokens = collectMediaUrlTokens(urlEntity);
        if (!isNonEmptyArray(mediaTokens)) {
          continue;
        }
        const validatedUrlEntity = TweetUrlSchema.safeParse(urlEntity);
        if (!validatedUrlEntity.success) {
          html = removeUrlTokensFromHtml(html, mediaTokens);
          continue;
        }

        const data = validatedUrlEntity.data;
        const candidates = [data.url, data.expandedUrl || data.url, data.displayUrl || data.url].filter(
          (value): value is string => typeof value === 'string' && value.length > 0
        );
        html = removeUrlTokensFromHtml(html, candidates);
      }
    }
  }
  if (mediaLinkedTokens.size > 0 && Array.isArray(cardUrls)) {
    try {
      const keep = cardUrls.filter((c) => {
        const candidates = [c.url, c.expandedUrl, c.displayUrl].filter(
          (value): value is string => typeof value === 'string' && value.length > 0
        );
        if (candidates.some((candidate) => mediaLinkedTokens.has(candidate))) {
          return false;
        }
        const expanded = c.expandedUrl || c.url;
        const display = c.displayUrl || c.url;
        return !(isTwitterImageUrl(expanded) || isTwitterImageUrl(display));
      });
      cardUrls.splice(0, cardUrls.length, ...keep);
    } catch {
      // Ignore normalization errors; media-linked tokens will be handled elsewhere.
    }
  }

  if (!hasRenderableMedia && apiEntities?.urls) {
    for (const urlEntity of apiEntities.urls) {
      const mediaTokens = collectMediaUrlTokens(urlEntity);
      const normalized = normalizeUrlEntity(urlEntity);
      if (!normalized) {
        continue;
      }
      const url = normalized.url;
      const expanded = normalized.expandedUrl || url;
      const display = normalized.displayUrl || expanded;
      const isPic =
        (expanded && (expanded.includes('pic.twitter.com') || expanded.includes('pic.x.com'))) ||
        (display && (display.includes('pic.twitter.com') || display.includes('pic.x.com')));
      if (!(isPic || isNonEmptyArray(mediaTokens))) continue;

      const esc = escapeRegExp(url);
      const placeholder = `<span class="twitter-image-placeholder" data-twitter-image-url="${url}" data-display-url="${display}"></span>`;
      const anchorRegex = regex.as(String.raw`<a[^>]*href="${esc}"[^>]*>[\s\S]*?</a>`, 'g');
      const replaced = html.replaceAll(anchorRegex, placeholder);
      if (replaced === html) {
        const urlPattern = regex.as(`(?<!data-twitter-image-url=")${esc}`, 'g');
        let next = html.replaceAll(urlPattern, placeholder);
        if (next === html && display && display !== url) {
          const displayEsc = escapeRegExp(display);
          const displayPattern = regex.as(`(?<!data-display-url=")${displayEsc}`, 'g');
          next = next.replaceAll(displayPattern, placeholder);
        }
        html = next;
      } else {
        html = replaced;
      }
    }
  }

  // If we are going to render a rich link preview card for a URL, remove the
  // corresponding t.co anchor from the tweet text to avoid duplicate rendering.
  // "Same" is determined via the expanded URL (host + path), because the
  // inline card often points at the resolved destination while the text still
  // contains the original t.co.
  if (Array.isArray(cardUrls) && cardUrls.length > 0 && Array.isArray(apiEntities?.urls)) {
    const cardTargets = new Set<string>();
    for (const card of cardUrls) {
      const source = card.expandedUrl || card.url;
      if (typeof source !== 'string' || source.length === 0) {
        continue;
      }
      const normalized = normalizeUrlForComparison(source);
      if (typeof normalized === 'string' && normalized.length > 0) {
        cardTargets.add(normalized);
      }
    }

    for (const raw of apiEntities.urls) {
      const urlEntity = TweetUrlSchema.safeParse(raw);
      if (!urlEntity.success) continue;
      const tco = urlEntity.data.url; // original short URL
      const expanded = urlEntity.data.expandedUrl || urlEntity.data.unwoundUrl || urlEntity.data.url;
      if (!(tco && expanded)) continue;
      const normalized = normalizeUrlForComparison(expanded);
      if (!normalized) continue;
      if (!cardTargets.has(normalized)) continue;
      // Remove the anchor (be liberal: match either t.co href or expanded href)
      const escTco = escapeRegExp(tco);
      const escExpanded = escapeRegExp(expanded);
      const anchorTco = regex.as(String.raw`<a[^>]*href="${escTco}"[^>]*>[\s\S]*?</a>`, 'g');
      const anchorExpanded = regex.as(String.raw`<a[^>]*href="${escExpanded}"[^>]*>[\s\S]*?</a>`, 'g');
      let next = html.replaceAll(anchorTco, '').replaceAll(anchorExpanded, '');
      // If still present as plain text (unlinked), remove the visible t.co token
      if (next === html) {
        next = next.replaceAll(regex.as(escTco, 'g'), '');
      }
      html = next.trim();
    }
  }

  // Fallback: If the API omitted entities but we know this tweet has photo
  // attachments (from mediaItems) and we still have no renderable media, try
  // to replace the first t.co anchor with a lazy-resolved image placeholder.
  if (
    !hasRenderableMedia &&
    isNonEmptyArray(normalizedMediaItems) &&
    normalizedMediaItems.some((item) => item.type === 'photo')
  ) {
    const tcoAnchor = html.match(TCO_ANCHOR_CAPTURE_PATTERN);
    if (tcoAnchor?.[1]) {
      const tco = tcoAnchor[1];
      const placeholder = `<span class="twitter-image-placeholder" data-twitter-image-url="${tco}" data-display-url="${tco}"></span>`;
      html = html.replace(tcoAnchor[0], placeholder);
    }
  }

  // Process metadata for t.co URLs if available (affects display text only)
  if (metadata) {
    for (const [url, meta] of Object.entries(metadata)) {
      if (meta?.url && url.includes('t.co')) {
        // Update display URLs in the HTML
        try {
          const resolvedUrlObject = new URL(meta.url);
          const displayUrl =
            resolvedUrlObject.hostname + (resolvedUrlObject.pathname === '/' ? '' : resolvedUrlObject.pathname);
          const truncatedDisplay = displayUrl.length > 30 ? `${displayUrl.slice(0, 27)}...` : displayUrl;

          // Replace in HTML if the t.co URL is visible
          const escapedUrl = escapeRegExp(url);
          const tcoRegex = regex.as(`(<a[^>]*href="${escapedUrl}"[^>]*>)[^<]+(</a>)`, 'g');
          html = html.replaceAll(tcoRegex, `$1${truncatedDisplay}$2`);
        } catch {
          // Ignore URL parsing errors
        }
      }
    }
  }

  // Final sanitization: remove t.co anchors for pic.* and direct media, and remove raw t.co for those cases
  const cleanupTco = (tco: string) => {
    const esc = escapeRegExp(tco);
    const anchorAny = regex.as(String.raw`<a[^>]*href="${esc}"[^>]*>[\s\S]*?</a>`, 'g');
    html = html.replaceAll(anchorAny, '');
    html = html.replaceAll(regex.as(esc, 'g'), '');
  };

  for (const e of apiEntities?.urls || []) {
    const validatedEntity = TweetUrlSchema.safeParse(e);
    if (!validatedEntity.success) {
      continue;
    }

    const data = validatedEntity.data;
    const tco = data.url;
    const exp = data.expandedUrl;
    const disp = data.displayUrl;
    if (!tco) {
      continue;
    }
    if (
      (exp && (exp.includes('pic.twitter.com') || exp.includes('pic.x.com'))) ||
      (disp && (disp.includes('pic.twitter.com') || disp.includes('pic.x.com')))
    ) {
      cleanupTco(tco);
    }
    if (exp && MEDIA_FILE_EXTENSION_PATTERN.test(exp)) {
      // replace anchor with expanded link already attempted; if still present as t.co, remove
      const esc = escapeRegExp(tco);
      const anchor = regex.as(String.raw`<a[^>]*href="${esc}"[^>]*>[\s\S]*?</a>`, 'g');
      const replacement = `<a href="${exp}" target="_blank" rel="noopener noreferrer" class="text-primary hover:underline">${data.displayUrl || exp}</a>`;
      if (anchor.test(html)) {
        html = html.replace(anchor, replacement);
      }
    }
  }

  // Trim trailing line breaks introduced by link removal
  html = html.replaceAll(TRAILING_BREAK_PATTERN, '').trim();

  return { html, cardUrls, inlineTweetUrls };
}

const NOTE_TWEET_COLLAPSE_ELLIPSIS = '…';

const findTruncationBoundary = (value: string, limit: number): number => {
  if (value.length <= limit) {
    return value.length;
  }

  const whitespaceBoundary = Math.max(value.lastIndexOf('\n', limit), value.lastIndexOf(' ', limit));
  if (whitespaceBoundary >= Math.floor(limit * 0.6)) {
    return whitespaceBoundary;
  }

  return limit;
};

export function buildCollapsedTweetHtml(parseableTweet: ParseableTweet, charLimit: number): string | null {
  if (!Number.isFinite(charLimit) || charLimit <= 0) {
    return null;
  }

  const baseText = typeof parseableTweet.text === 'string' ? parseableTweet.text : '';
  if (baseText.length <= charLimit) {
    return null;
  }

  const boundary = findTruncationBoundary(baseText, charLimit);
  const sliced = baseText.slice(0, boundary).trimEnd();
  const truncatedText = `${sliced}${NOTE_TWEET_COLLAPSE_ELLIPSIS}`;

  const truncatedParseable: ParseableTweet = {
    ...parseableTweet,
    text: truncatedText,
  };

  const { html } = parseTweetText(truncatedParseable);
  return html;
}

export type { TweetEntities } from '@/types/tweet';
